import { useEffect } from "react";
import analyticsService from "../AnalyticsService";
import { resolveEnvironment } from "../constants";
import { resolvePageName } from "./resolvePageName";

const ignoreImportError = Boolean;

const APP_EVENT_NAMES = [
  "click",
  "keydown",
  "submit",
  "change",
  "input",
  "scroll",
  "touchstart",
  "pointerdown",
  "pointerup",
];

const WEB_VITALS_METRICS = [
  { onName: "onCLS", getName: "getCLS", metric: "cls", unit: "score" },
  { onName: "onFCP", getName: "getFCP", metric: "fcp", unit: "ms" },
  { onName: "onLCP", getName: "getLCP", metric: "lcp", unit: "ms" },
  { onName: "onTTFB", getName: "getTTFB", metric: "ttfb", unit: "ms" },
  { onName: "onFID", getName: "getFID", metric: "fid", unit: "ms" },
  { onName: "onINP", getName: "getINP", metric: "inp", unit: "ms" },
];

const NAVIGATION_METRICS = [
  {
    name: "page_load_time",
    value: (entry) => entry.duration,
  },
  {
    name: "dns_lookup_time",
    value: (entry) => (entry.domainLookupEnd || 0) - (entry.domainLookupStart || 0),
  },
  {
    name: "tcp_connect_time",
    value: (entry) => (entry.connectEnd || 0) - (entry.connectStart || 0),
  },
  {
    name: "request_time",
    value: (entry) => (entry.responseStart || 0) - (entry.requestStart || 0),
  },
  {
    name: "response_download_time",
    value: (entry) => (entry.responseEnd || 0) - (entry.responseStart || 0),
  },
];

const SUMMARY_METRICS = [
  {
    name: "app_event_count",
    unit: "count",
    value: ({ eventCount }) => eventCount,
  },
  {
    name: "app_event_rate_per_minute",
    unit: "count_per_minute",
    value: ({ eventCount, durationMs }) => (eventCount * 60 * 1000) / durationMs,
  },
  {
    name: "app_event_tracking_duration",
    unit: "ms",
    value: ({ durationMs }) => durationMs,
  },
  {
    name: "long_task_count",
    unit: "count",
    value: ({ longTaskCount }) => longTaskCount,
  },
  {
    name: "long_task_total_duration",
    unit: "ms",
    value: ({ longTaskDuration }) => longTaskDuration,
  },
  {
    name: "long_task_total_blocking_time",
    unit: "ms",
    value: ({ longTaskBlocking }) => longTaskBlocking,
  },
];

const getPagePath = () => {
  return `${globalThis.location.pathname}${globalThis.location.search}`;
};

const getPageName = (pagePath) => {
  return (
    resolvePageName(pagePath) ||
    document?.title ||
    globalThis.location.pathname
  );
};

const trackPerfMetric = (
  name,
  value,
  unit,
  triggerPoint,
) => {
  const pagePath = getPagePath();

  const numericValue = Number(value?.toFixed ? value.toFixed(3) : value);
  if (!Number.isFinite(numericValue)) {
    return;
  }

  analyticsService.trackPerformance({
    metric_name: name,
    metric_value: numericValue,
    metric_unit: unit,
    trigger_point: triggerPoint,
    environment: analyticsService?.context?.environment || resolveEnvironment(),
    page_name: getPageName(pagePath),
    page_path: pagePath,
  });
};

const subscribeMetric = (metrics, onName, getName, handler) => {
  if (typeof metrics?.[onName] === "function") {
    metrics[onName](handler);
    return;
  }

  if (typeof metrics?.[getName] === "function") {
    metrics[getName](handler);
  }
};

const trackNavigationBreakdown = (entry) => {
  if (!entry) {
    return;
  }

  const navigationStart =
    entry.startTime || entry.fetchStart || entry.navigationStart || 0;

  NAVIGATION_METRICS.forEach(({ name, value }) => {
    trackPerfMetric(name, value(entry), "ms", "navigation_observer");
  });
  trackPerfMetric(
    "dom_interactive_time",
    (entry.domInteractive || 0) - navigationStart,
    "ms",
    "navigation_observer",
  );
  trackPerfMetric(
    "dom_content_loaded_time",
    (entry.domContentLoadedEventEnd || 0) - navigationStart,
    "ms",
    "navigation_observer",
  );
  trackPerfMetric(
    "dom_complete_time",
    (entry.domComplete || 0) - navigationStart,
    "ms",
    "navigation_observer",
  );
};

const trackPaintMetric = (entry) => {
  if (!entry?.name) {
    return;
  }

  if (entry.name === "first-paint") {
    trackPerfMetric("fp", entry.startTime, "ms", "paint_observer");
  }

  if (entry.name === "first-contentful-paint") {
    trackPerfMetric("fcp_paint", entry.startTime, "ms", "paint_observer");
  }
};

const safeObserveEntryType = (observer, entryType) => {
  if (!observer || typeof observer.observe !== "function") {
    return false;
  }

  try {
    observer.observe({ type: entryType, buffered: true });
    return true;
  } catch {
    return false;
  }
};

const registerWebVitals = (metrics) => {
  WEB_VITALS_METRICS.forEach(({ onName, getName, metric, unit }) => {
    subscribeMetric(metrics, onName, getName, (metricData) =>
      trackPerfMetric(metric, metricData?.value, unit, "web_vitals_callback"),
    );
  });
};

const trackLongTask = (entry, longTaskCountRef, longTaskDurationRef, longTaskBlockingRef) => {
  longTaskCountRef.current += 1;
  longTaskDurationRef.current += Number(entry.duration || 0);
  longTaskBlockingRef.current += Math.max(0, Number(entry.duration || 0) - 50);
};

const flushSummaryMetrics = (
  triggerPoint,
  appEventCountRef,
  trackingStartRef,
  longTaskCountRef,
  longTaskDurationRef,
  longTaskBlockingRef,
) => {
  const durationMs = Math.max(1, Date.now() - trackingStartRef.current);
  const eventCount = appEventCountRef.current;

  const summaryContext = {
    durationMs,
    eventCount,
    longTaskCount: longTaskCountRef.current,
    longTaskDuration: longTaskDurationRef.current,
    longTaskBlocking: longTaskBlockingRef.current,
  };

  SUMMARY_METRICS.forEach(({ name, unit, value }) => {
    trackPerfMetric(name, value(summaryContext), unit, triggerPoint);
  });
};

export const usePerformanceTracker = () => {
  useEffect(() => {
    let observer = null;
    const appEventCountRef = { current: 0 };
    const trackingStartRef = { current: Date.now() };
    const longTaskCountRef = { current: 0 };
    const longTaskDurationRef = { current: 0 };
    const longTaskBlockingRef = { current: 0 };

    import("web-vitals")
      .then((metrics) => {
        registerWebVitals(metrics);
      })
      .catch(ignoreImportError);

    if (globalThis.PerformanceObserver) {
      observer = new PerformanceObserver((entryList) => {
        entryList.getEntries().forEach((entry) => {
          if (entry.entryType === "navigation") {
            trackNavigationBreakdown(entry);
            return;
          }

          if (entry.entryType === "paint") {
            trackPaintMetric(entry);
            return;
          }

          if (entry.entryType === "longtask") {
            trackLongTask(
              entry,
              longTaskCountRef,
              longTaskDurationRef,
              longTaskBlockingRef,
            );
          }
        });
      });

      safeObserveEntryType(observer, "navigation");
      safeObserveEntryType(observer, "paint");
      safeObserveEntryType(observer, "longtask");
    } else {
      const fallbackNavigationEntry =
        globalThis.performance?.getEntriesByType?.("navigation")?.[0];
      trackNavigationBreakdown(fallbackNavigationEntry);

      const paintEntries = globalThis.performance?.getEntriesByType?.("paint") || [];
      paintEntries.forEach((entry) => trackPaintMetric(entry));
    }

    if (globalThis.performance?.memory?.usedJSHeapSize) {
      trackPerfMetric(
        "memory_used_js_heap",
        globalThis.performance.memory.usedJSHeapSize,
        "bytes",
        "memory_snapshot",
      );
    }

    const onAppEvent = () => {
      appEventCountRef.current += 1;
    };

    const flushAppEventSummary = (triggerPoint) => {
      flushSummaryMetrics(
        triggerPoint,
        appEventCountRef,
        trackingStartRef,
        longTaskCountRef,
        longTaskDurationRef,
        longTaskBlockingRef,
      );
    };

    APP_EVENT_NAMES.forEach((eventName) => {
      globalThis.addEventListener(eventName, onAppEvent, { passive: true });
    });

    const handlePageHide = () => {
      flushAppEventSummary("app_event_summary_pagehide");
    };

    const handleBeforeUnload = () => {
      flushAppEventSummary("app_event_summary_beforeunload");
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        flushAppEventSummary("app_event_summary_hidden");
      }
    };

    globalThis.addEventListener("pagehide", handlePageHide);
    globalThis.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      flushAppEventSummary("app_event_summary_unmount");
      APP_EVENT_NAMES.forEach((eventName) => {
        globalThis.removeEventListener(eventName, onAppEvent);
      });
      globalThis.removeEventListener("pagehide", handlePageHide);
      globalThis.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      observer?.disconnect();
    };
  }, []);
};
